// @react-compiler enable
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {SingleSelectToken} from '@github-ui/shared-single-select-components/SingleSelectToken'
import {testIdProps} from '@github-ui/test-id-props'
import {Token, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import {memo} from 'react'

import type {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import type {MultiSelectValue} from '../../../api/columns/contracts/multi-select'
import type {PersistedOption} from '../../../api/columns/contracts/single-select'
import {TableRowHeight} from '../../../api/view/contracts'
import {useTableRowHeight} from '../../../hooks/use-table-row-height'
import type {ColumnModelForDataType} from '../../../models/column-model'
import {type ColumnValue, hasValue, isLoading} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {PillPlaceholder} from '../../common/placeholders'
import {BaseCell} from '../cells/base-cell'
import {DropdownCell} from '../cells/dropdown-cell'
import {useRecordCellRenderer} from '../performance-measurements'
import styles from './multi-select-renderer.module.css'
import {useTokenRowOverflow} from './use-token-row-overflow'

// Horizontal gap between tokens. Keep in sync with `--base-size-4` (4px) used in
// the stylesheet so the overflow measurement matches what the browser lays out.
const TOKEN_GAP_PX = 4

// Reserved width for the `+N` overflow badge. A fixed estimate keeps the
// measurement cheap (no second measured element) and errs on the side of
// reserving slightly too much, so the badge itself is never clipped.
const OVERFLOW_BADGE_WIDTH_PX = 48

type Props = Readonly<{
  currentValue: ColumnValue<MultiSelectValue>
  model: MemexItemModel
  options: ColumnModelForDataType<typeof MemexColumnDataType.MultiSelect>['settings']['options']
  dropdownRef?: React.RefObject<HTMLButtonElement | null>
  isDisabled?: boolean
}>

export const LoadingMultiSelectCell = () => (
  <BaseCell>
    <PillPlaceholder minWidth={40} maxWidth={80} {...testIdProps('placeholder')} />
  </BaseCell>
)

/**
 * Resolves the selected option IDs against the column's option settings,
 * preserving the order in which the options were configured. Unknown IDs are
 * dropped (the option may have been deleted after the value was saved).
 */
function selectedOptions(value: MultiSelectValue, options: ReadonlyArray<PersistedOption>): Array<PersistedOption> {
  if (value.options.length === 0 || options.length === 0) return []

  const selectedIds = new Set(value.options.map(o => o.id))
  return options.filter(option => selectedIds.has(option.id))
}

type MultiSelectOverflowBadgeProps = Readonly<{hiddenOptions: ReadonlyArray<PersistedOption>}>

/**
 * Non-interactive `+N` count pill shown when selected tokens overflow the
 * cell. Mirrors the visual language of the issues-index metadata overflow
 * badge. Making it open the hidden options is tracked as a follow-up.
 */
function MultiSelectOverflowBadge({hiddenOptions}: MultiSelectOverflowBadgeProps) {
  const count = hiddenOptions.length
  const names = hiddenOptions.map(option => option.name).join(', ')

  return (
    <>
      <Token
        as="span"
        text={`+${count}`}
        title={names}
        className={styles.OverflowBadge}
        {...testIdProps('multi-select-overflow-token')}
      />
      {/* The visible `+N` badge conveys only the count, not which options are
          hidden. Surface the hidden option names to mouse users via `title` on
          the badge, and to screen reader and keyboard users via visually hidden
          text, so the information is not sighted-mouse only. */}
      <VisuallyHidden>{names}</VisuallyHidden>
    </>
  )
}

export const MultiSelectRenderer = memo(function MultiSelectRendererComponent({
  currentValue,
  model,
  dropdownRef,
  options,
  isDisabled,
}: Props) {
  useRecordCellRenderer('MultiSelectRenderer', model.id)
  const {rowHeight} = useTableRowHeight()
  const isWrapping = rowHeight !== TableRowHeight.Single
  // Gates the DOM measurement that powers the `+N` overflow badge. Off by
  // default: while disabled the row renders every token and lets the cell clip
  // the overflow (like the labels renderer), so there is no measurement cost.
  // The flag lets us A/B the measurement's performance before enabling it, and
  // keeps the hook reusable for other multi-token cells (e.g. labels).
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const overflowBadgeEnabled = useFeatureFlag('memex_token_row_overflow')

  const matchedOptions =
    !isLoading(currentValue) && hasValue(currentValue) ? selectedOptions(currentValue.value, options) : []

  const {
    ref: tokenListRef,
    visibleCount,
    measured,
  } = useTokenRowOverflow({
    optionCount: matchedOptions.length,
    // Include names, not just ids: a rename changes token width (and therefore
    // the fit) without changing the selected option set.
    signature: matchedOptions.map(option => `${option.id}:${option.name}`).join('\u0000'),
    gap: TOKEN_GAP_PX,
    badgeWidth: OVERFLOW_BADGE_WIDTH_PX,
    // Wrapping (expanded) rows already flow tokens onto multiple lines, so no
    // overflow badge is needed there. The feature flag gates the measurement so
    // it can be rolled out and A/B tested independently.
    enabled: !isWrapping && overflowBadgeEnabled,
  })

  if (isLoading(currentValue)) {
    return <LoadingMultiSelectCell />
  }

  if (matchedOptions.length === 0) {
    return <DropdownCell ref={dropdownRef} isDisabled={isDisabled} />
  }

  // While a measurement pass is in flight the full list must be on screen so the
  // hook can read every token width. Only slice once measured; this also keeps
  // the row from flashing an empty or stale slice on the load → loaded
  // transition (the measured counts start at zero until the row is measured).
  const showAll = isWrapping || !measured
  const visibleOptions = showAll ? matchedOptions : matchedOptions.slice(0, visibleCount)
  const hiddenOptions = showAll ? [] : matchedOptions.slice(visibleCount)

  return (
    <DropdownCell ref={dropdownRef} isDisabled={isDisabled}>
      <div
        ref={tokenListRef}
        className={clsx(
          styles.TokenList,
          isWrapping && styles.wrapping,
          hiddenOptions.length > 0 && styles.hasOverflow,
        )}
        {...testIdProps('multi-select-token-list')}
      >
        {visibleOptions.map(option => (
          <SingleSelectToken key={option.id} option={option} />
        ))}
        {hiddenOptions.length > 0 && <MultiSelectOverflowBadge hiddenOptions={hiddenOptions} />}
      </div>
    </DropdownCell>
  )
})
